<script setup lang="ts">
import RlUploadPdf from '@/components/common/upload/RlUploadPdf.vue'
import type { IPolicyGroupForm } from '@/types/asicw/policies'
import { Icon } from '@iconify/vue'
import { computed, h, reactive, ref } from 'vue'
import { DateTime } from 'luxon'
import type { FormRules } from 'element-plus'
import { useOIDCStore } from '@/stores/oidc.store'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'

const { isManagerUser } = useOIDCStore()

const form = defineModel<IPolicyGroupForm>({ required: true })

const calendarIcon = h(Icon, { icon: 'mdi:calendar-month-outline' })

const termsOptions = [1, 2, 3, 6, 12, 18, 24, 36]

const rules = reactive<FormRules<IPolicyGroupForm>>({
  groupNumber: [{ required: true, message: 'Please provide a group number', trigger: 'blur' }],
  creationDate: [{ required: true, message: 'Please pick a creation date', trigger: ['blur', 'change'] }],
  serviceAgreement: [{ required: true, message: 'Please upload a pdf file', trigger: 'blur' }],
})

const isFormValid = computed(
  () =>
    !!form.value.groupNumber && !!form.value.creationDate && !!form.value.serviceAgreement && !!form.value.customerID
)

const fileUploaderRef = ref()

const onCustomerChange = (customer: any) => {
  form.value.customerID = customer?.id
}

const reset = () => {
  fileUploaderRef.value.reset()
}

defineExpose({ reset, isFormValid })
</script>

<template>
  <el-form
    :label-width="192"
    label-position="left"
    :rules="rules"
    :hide-required-asterisk="true"
    class="w-full px-8"
    :model="form"
  >
    <el-scrollbar height="460px">
      <el-form-item>
        <template #label>
          <div class="flex items-center gap-2">
            <el-text class="text-text-tertiary">Group Number</el-text>
            <el-tooltip
              class="box-item"
              effect="dark"
              content="Alphanumeric text that uniquely identifies the policy group"
              placement="top"
            >
              <Icon icon="material-symbols:help-outline" height="16" />>
            </el-tooltip>
          </div>
        </template>
        <el-input v-model="form.groupNumber" placeholder="Group Number" clearable />
      </el-form-item>

      <el-form-item v-if="isManagerUser" label="Customer">
        <RcCustomerSelect @change="onCustomerChange" />
      </el-form-item>

      <!-- Creation date -->
      <el-form-item label="Creation date" prop="creationDate"
        ><el-date-picker
          v-model="form.creationDate"
          :prefix-icon="calendarIcon"
          type="datetime"
          placeholder="Pick a Date"
          format="MM/DD/YYYY"
      /></el-form-item>

      <!-- Initial term -->
      <el-form-item label="Initial term">
        <div class="flex items-center gap-2">
          <el-select v-model="form.initialTerm" class="w-[160px]">
            <el-option v-for="opt in termsOptions" :key="opt" :label="`${opt} months`" :value="opt" />
          </el-select>
          <span v-if="form.creationDate && form.initialTerm" class="text-text-tertiary whitespace-nowrap"
            >till {{ DateTime.fromJSDate(form.creationDate).plus({ months: form.initialTerm }).toLocaleString() }}</span
          >
        </div>
      </el-form-item>

      <!-- Subsequent term -->
      <el-form-item label="Subsequent term">
        <div class="flex items-center gap-2">
          <el-select v-model="form.subsequentTerm" class="w-[160px]">
            <el-option v-for="opt in termsOptions" :key="opt" :label="`${opt} months`" :value="opt" />
          </el-select>
          <span v-if="form.creationDate && form.subsequentTerm" class="text-text-tertiary whitespace-nowrap"
            >till
            {{
              DateTime.fromJSDate(form.creationDate)
                .plus({ months: form.initialTerm })
                .plus({ months: form.subsequentTerm })
                .toLocaleString()
            }}</span
          >
        </div>
      </el-form-item>

      <!-- Service agreement -->
      <el-form-item label="Service agreement" prop="serviceAgreement">
        <RlUploadPdf ref="fileUploaderRef" v-model="form.serviceAgreement" :max-size-mb="10" />
      </el-form-item>
    </el-scrollbar>
  </el-form>
</template>
